import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router';
import {
  Streamdown,
  type Components as StreamdownComponents,
  type PluginConfig as StreamdownPluginConfig,
} from 'streamdown';

import type {
  AgentCoreAttachment,
  AgentCoreMessageContext,
} from '@/shared/api/agentCore/agentCoreApi';
import api from '@/shared/api/api';
import { useAppSelector } from '@/shared/hooks';
import { getName, getFileSrc } from '@/shared/lib';
import { Icon, Spinner } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { reportsModel } from '@/entities/reports';
import { userModel } from '@/entities/user';

import { patientAgentMessages } from '../config';
import {
  formatMessageHoverStamp,
  getReportContextLabel,
  getMessageResourceLink,
} from '../lib';

import styles from './AgentChat.module.scss';

const StreamdownCopyIcon = ({
  className,
  size = 16,
}: {
  className?: string;
  size?: number;
}) => <Icon name="copy" className={className} size={size} />;

const StreamdownCheckIcon = ({
  className,
  size = 16,
}: {
  className?: string;
  size?: number;
}) => <Icon name="check" className={className} size={size} />;

/**
 * One sent attachment. The whole widget is interactive and opens the
 * attachment viewer. Images render as a bare square preview tile — same as
 * the composer's image attachment — with the thumbnail loaded through the
 * chat-upload download endpoint. Non-image files keep the document-style
 * card (icon tile + name).
 */
const MessageAttachmentCard: FC<{
  attachment: AgentCoreAttachment;
  onPreview?: (attachment: AgentCoreAttachment) => void;
}> = ({ attachment, onPreview }) => {
  const isImage = attachment.mime.startsWith('image/');
  const [thumbURL, setThumbURL] = useState('');
  const [thumbFailed, setThumbFailed] = useState(false);

  useEffect(() => {
    if (!isImage) {
      return;
    }
    let active = true;
    void api.assets
      .getChatUploadAsset({ AssetID: attachment.assetID })
      .then((response) => {
        const kind = response.Asset?.Kind;
        if (
          kind?.case === 'ChatUpload' &&
          kind.value.Content?.case === 'File'
        ) {
          const fileID = kind.value.Content.value.File?.ID;
          if (active && fileID) {
            setThumbURL(getFileSrc(fileID));
          }
        }
      })
      .catch(() => {
        // No thumbnail — the doc-icon fallback renders.
        if (active) {
          setThumbFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [attachment.assetID, isImage]);

  if (isImage) {
    return (
      <button
        type="button"
        className={cn(styles.attachmentTile, styles.attachmentCardInteractive)}
        title={attachment.name}
        onClick={() => onPreview?.(attachment)}
      >
        {thumbURL ? (
          <img src={thumbURL} alt="" onError={() => setThumbFailed(true)} />
        ) : thumbFailed ? (
          <Icon name="doc" size={20} />
        ) : (
          <Spinner size={14} className={styles.attachmentTileSpinner} />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={cn(styles.attachmentCard, styles.attachmentCardInteractive)}
      title={attachment.name}
      onClick={() => onPreview?.(attachment)}
    >
      <div className={styles.attachmentThumb}>
        <Icon name="doc" size={20} />
      </div>
      <div className={styles.attachmentCardInfo}>
        <span className={styles.attachmentCardName} title={attachment.name}>
          {attachment.name}
        </span>
      </div>
    </button>
  );
};

type MessageItem = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt?: string;
  selectionContext?: string;
  authorID?: string;
  attachments?: AgentCoreAttachment[];
  messageContext?: AgentCoreMessageContext;
};

type MessageArticleProps = {
  item: MessageItem;
  /** Streamdown key must change when asset references resolve asynchronously. */
  streamdownKey: string;
  citationComponents: StreamdownComponents;
  /** Streamdown plugin config; carries the onboarding flow renderer. */
  streamdownPlugins?: StreamdownPluginConfig;
  documentMode: boolean;
  isRunning: boolean;
  selectedDocumentText: string;
  dismissedProposals: Set<string>;
  className?: string;
  onReplaceDocumentSelection?: (text: string) => boolean;
  onInsertBelowDocumentSelection?: (text: string) => boolean;
  onDismissProposal: (itemID: string) => void;
  onAttachmentPreviewChange?: (attachment: AgentCoreAttachment) => void;
};

/** One user or assistant message: bubbles, attachments and rewrite cards. */
export const MessageArticle: FC<MessageArticleProps> = ({
  item,
  streamdownKey,
  citationComponents,
  streamdownPlugins,
  documentMode,
  isRunning,
  selectedDocumentText,
  dismissedProposals,
  onReplaceDocumentSelection,
  onInsertBelowDocumentSelection,
  onDismissProposal,
  onAttachmentPreviewChange,
  className,
}) => {
  const intl = useIntl();
  const currentUser = useAppSelector(userModel.selectors.selectCurrentUser);
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);
  const staffMembers = useAppSelector(
    organizationModel.selectors.selectAllUsers,
  );

  const author = item.authorID
    ? staffMembers.find((current) => current.UserID === item.authorID)
    : undefined;
  const userAuthorLabel = author
    ? getName(author.PersonalData?.FirstName, author.PersonalData?.LastName, {
        userLocale,
      }).trim()
    : item.authorID && item.authorID === currentUser?.ID
      ? getName(
          currentUser?.PersonalData?.FirstName,
          currentUser?.PersonalData?.LastName,
          { userLocale },
        ).trim()
      : '';

  const reports = useAppSelector(reportsModel.selectors.selectAll);
  const messageContext = item.messageContext;
  const patientLabel = messageContext?.patient_id
    ? messageContext.label ||
      intl.formatMessage(patientAgentMessages.workContextPatient)
    : '';
  const resources = (messageContext?.resources ?? []).flatMap((resource) => {
    const report =
      resource.type === 'report'
        ? reports.find((candidate) => candidate.ID === resource.id)
        : undefined;
    const name = report ? getReportContextLabel(report) : resource.name;
    return name
      ? [
          {
            ...resource,
            name,
            href: messageContext
              ? getMessageResourceLink(resource, messageContext, report)
              : undefined,
          },
        ]
      : [];
  });

  return (
    <article
      className={cn(
        styles.message,
        item.role === 'user' && styles.messageUser,
        item.role === 'assistant' && styles.messageAssistant,
        className,
      )}
    >
      {item.role === 'user' ? (
        <div className={cn('p3', styles.messageUserHeader)}>
          <time
            className={cn(
              styles.messageHoverStamp,
              item.createdAt &&
                formatMessageHoverStamp(item.createdAt, intl) &&
                styles.messageHoverStampVisible,
            )}
            dateTime={item.createdAt}
          >
            {item.createdAt
              ? formatMessageHoverStamp(item.createdAt, intl)
              : ''}
          </time>
          {userAuthorLabel ? (
            <span data-ph-mask className={styles.messageRole}>
              {userAuthorLabel}
            </span>
          ) : null}
        </div>
      ) : null}
      {item.role === 'assistant' ? (
        <>
          <Streamdown
            key={streamdownKey}
            className={cn('p2', styles.markdownMessage)}
            components={citationComponents}
            plugins={streamdownPlugins}
            controls={{
              code: { copy: true, download: false },
            }}
            icons={{
              CheckIcon: StreamdownCheckIcon,
              CopyIcon: StreamdownCopyIcon,
            }}
            lineNumbers={false}
            mode="streaming"
            animated={false}
          >
            {item.content}
          </Streamdown>
          {documentMode &&
          item.selectionContext &&
          !isRunning &&
          !dismissedProposals.has(item.id) ? (
            <div className={styles.rewriteCard}>
              <strong>
                {intl.formatMessage(patientAgentMessages.proposedRewrite)}
              </strong>
              <del>{item.selectionContext}</del>
              <p>{item.content}</p>
              {selectedDocumentText !== item.selectionContext ? (
                <small>
                  {intl.formatMessage(patientAgentMessages.reselectRewrite)}
                </small>
              ) : null}
              <div>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  disabled={selectedDocumentText !== item.selectionContext}
                  onClick={() => {
                    if (onReplaceDocumentSelection?.(item.content)) {
                      onDismissProposal(item.id);
                    }
                  }}
                >
                  {intl.formatMessage(patientAgentMessages.replaceSelection)}
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  disabled={selectedDocumentText !== item.selectionContext}
                  onClick={() => {
                    if (onInsertBelowDocumentSelection?.(item.content)) {
                      onDismissProposal(item.id);
                    }
                  }}
                >
                  {intl.formatMessage(patientAgentMessages.insertBelow)}
                </button>
                <button
                  type="button"
                  onClick={() => onDismissProposal(item.id)}
                >
                  {intl.formatMessage(patientAgentMessages.discard)}
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className={styles.userBubble}>
            {patientLabel || resources.length ? (
              <div
                className={styles.messageContext}
                data-component="message-context"
                data-ph-mask
              >
                <span className={styles.messageContextPill}>
                  {patientLabel && messageContext?.patient_id ? (
                    <>
                      <span
                        className={styles.scopePillAvatar}
                        aria-hidden="true"
                      >
                        {patientLabel
                          .split(' ')
                          .map((part) => part[0])
                          .filter(Boolean)
                          .slice(0, 2)
                          .join('')}
                      </span>
                      <Link
                        className={styles.scopePillName}
                        title={patientLabel}
                        to={`/patients/${encodeURIComponent(messageContext.patient_id)}`}
                      >
                        {patientLabel}
                      </Link>
                    </>
                  ) : null}
                  {resources.map((resource, index) => (
                    <span
                      className={styles.messageContextResource}
                      key={`${resource.type}:${resource.id}`}
                    >
                      {patientLabel || index > 0 ? (
                        <span className={styles.scopePillDivider}>·</span>
                      ) : null}
                      {resource.href ? (
                        <Link
                          className={styles.scopePillEntity}
                          title={resource.name}
                          to={resource.href}
                          target={
                            resource.href.includes('/studio/')
                              ? '_blank'
                              : undefined
                          }
                          rel={
                            resource.href.includes('/studio/')
                              ? 'noopener noreferrer'
                              : undefined
                          }
                        >
                          {resource.name}
                        </Link>
                      ) : (
                        <span
                          className={styles.scopePillEntity}
                          title={resource.name}
                        >
                          {resource.name}
                        </span>
                      )}
                    </span>
                  ))}
                </span>
              </div>
            ) : null}
            <p className={cn('p2', styles.messageText)}>{item.content}</p>
            {item.attachments && item.attachments.length ? (
              <div className={styles.messageAttachments}>
                {item.attachments.map((attachment) => (
                  <MessageAttachmentCard
                    key={attachment.assetID}
                    attachment={attachment}
                    onPreview={onAttachmentPreviewChange}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </>
      )}
    </article>
  );
};
